import { fmtDateTimeFirm, firmZone } from "@/lib/dt";   // 5c · F-5; Batch 6 · E (AT-61): the zone on the banner
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { methodologyRefusal } from "@/components/methodology/MethodologyDeviationsPanel";   // STEP 7e-2 · S3
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import LockPreflightDialog from "@/components/LockPreflightDialog";   // Batch 11 · A6
import { AlertTriangle, ExternalLink, Lock, Unlock, ShieldCheck, FileArchive, FileText, Fingerprint } from "lucide-react";
import { asArray } from "@/lib/asArray";

/**
 * SignOffCard renders the partner sign-off UI for a single engagement.
 *
 * Props:
 *   - engagement: the engagement object (must include signed_off, signed_off_at, signed_off_by_name, signature_hash)
 *   - onChanged(): callback fired after a successful sign-off or unlock
 */
// Sequential sign-off stages. EQC is optional per engagement.
// Batch A2 — EQC step (when engagement.eqc_required) is inserted between
// Reviewer and Partner and is enabled only once 522(a) is concluded.
// STEP 3 · B-8c (owner 26-Oct-2026): no role list here — who may sign a stage is the sign-off bar's rule, answered by the
// server per engagement (GET /engagements/{id}/signoff-rights → review_roles.may_sign): Preparer / Reviewer = any team
// member, EQ reviewer = the appointed EQ reviewer, Partner = the engagement partner.
const STAGE_META = {
  preparer: { labelKey: "signOff.stagePreparer", hintKey: "signOff.stagePreparerHint" },
  reviewer: { labelKey: "signOff.stageReviewer", hintKey: "signOff.stageReviewerHint" },
  eqc:      { labelKey: "signOff.stageEqc",      hintKey: "signOff.stageEqcHint" },
  partner:  { labelKey: "signOff.stagePartner",  hintKey: "signOff.stagePartnerHint" },
};

// Batch A2 — returns the ordered stage list for this engagement. EQC step
// is skipped entirely when EQC is not required; when required it sits
// between Reviewer and Partner. The step is disabled until 522(a) is
// concluded. Reads `requires_eqc` (stored field) with a fallback to
// `eqc_required` for any legacy payload shape.
export function stageOrderFor(engagement) {
  // D18 — Preparer → Reviewer → EQ reviewer (522(a) completed and signed, when required) → Partner; locks at the Partner.
  const base = ["preparer", "reviewer"];
  const eqc = engagement?.requires_eqc ?? engagement?.eqc_required;
  if (eqc) base.push("eqc");
  base.push("partner");
  return base;
}

export default function SignOffCard({ engagement, onChanged }) {
  const { t, lang, dict } = useI18n();
  const { user } = useAuth() || {};   // 2.2: the card now renders on File Assembly too — no crash without an auth provider (tests)
  // Partner-only sign-off (Admin retained as super-admin escape hatch)
  const canManage = user?.role === "Admin" || user?.role === "Partner";
  const [signingStage, setSigningStage] = useState(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  // Batch A2 — use the exported helper as the single source of truth
  // for stage order (Preparer → Reviewer → EQC? → Partner). Removes the
  // prior bug where EQC was appended AFTER Partner in the render loop.
  const stageOrder = stageOrderFor(engagement);
  const wf = engagement?.signoff_workflow || {};

  // §5 R1: hard gate — refuse Partner sign-off while any active account is
  // unmapped. Legacy engagements (no framework) return {allowed:true, framework:""}
  // so nothing changes for them.
  const [preflight, setPreflight] = useState(null);
  useEffect(() => {
    if (!engagement?.id || engagement?.signed_off) { setPreflight(null); return; }
    let cancelled = false;
    api.get(`/fs/${engagement.id}/signoff-preflight`)
      .then((r) => { if (!cancelled) setPreflight(r.data); })
      .catch(() => { if (!cancelled) setPreflight(null); });
    return () => { cancelled = true; };
  }, [engagement?.id, engagement?.signed_off, engagement?.signoff_workflow]);

  // Silent defaults · A-15: a missing presentation framework blocks too (the server answers allowed: false + code)
  const gateBlocked = preflight && !preflight.allowed;
  const gateNoFramework = gateBlocked && preflight.code === "PREFLIGHT_FRAMEWORK_NOT_SET";
  const gateReason = gateBlocked ? ((lang === "ar" && preflight.reason_ar) || preflight.reason) : "";

  // ISQM 2.24(b), 27 report-date gate visibility — poll only on EQC-flagged
  // engagements so non-EQC workflows aren't touched at all.
  const [eqr, setEqr] = useState(null);
  useEffect(() => {
    if (!engagement?.id || !engagement?.requires_eqc || engagement?.signed_off) {
      setEqr(null); return;
    }
    let cancelled = false;
    api.get(`/isqm/eqr-status/${engagement.id}`, { _silent: true })
      .then((r) => { if (!cancelled) setEqr(r.data); })
      .catch(() => { if (!cancelled) setEqr(null); });
    return () => { cancelled = true; };
  }, [engagement?.id, engagement?.requires_eqc, engagement?.signed_off,
       engagement?.signoff_workflow]);

  // STEP 3 · B-8c: this user's answer per stage, from the server (the same rule the stage route enforces)
  const [rights, setRights] = useState(null);
  useEffect(() => {
    if (!engagement?.id || engagement?.signed_off) { setRights(null); return; }
    let cancelled = false;
    Promise.resolve()
      .then(() => api.get(`/engagements/${engagement.id}/signoff-rights`, { _silent: true }))
      .then((r) => { if (!cancelled) setRights((r && r.data && r.data.stages) || null); })
      .catch(() => { if (!cancelled) setRights(null); });
    return () => { cancelled = true; };
  }, [engagement?.id, engagement?.signed_off, engagement?.signoff_workflow, user?.user_id]);

  const eqrOk = eqr?.applies && eqr?.completed_satisfactorily;
  const eqrBlocked = eqr?.applies && !eqr?.completed_satisfactorily;

  // Batch 11 · A6: the Partner stage locks the file — the sign-off preflight (D10) is shown first
  const [lockAsk, setLockAsk] = useState(false);
  const doStageSignoff = async (stage, preflightDone = false) => {
    if (stage === "partner" && gateBlocked) {
      toast.error(gateReason);
      return;
    }
    if (stage === "partner" && !preflightDone) { setLockAsk(true); return; }
    setSigningStage(stage);
    try {
      const r = await api.post(`/engagements/${engagement.id}/stage-signoff`, { stage });
      const stageLabel = t(STAGE_META[stage].labelKey);
      asArray(r.data.warnings).slice(0, 6).forEach((w) => toast.warning(w));   // Batch 11 · A5: stages never refuse a missing level
      if (r.data.signature_hash) {
        toast.success(t("signOff.stageCompleteHash")
          .replace("{stage}", stageLabel)
          .replace("{hash}", r.data.signature_hash.slice(0, 8)));
      } else {
        toast.success(t("signOff.stageComplete").replace("{stage}", stageLabel));
      }
      onChanged && onChanged();
    } catch (e) {
      toast.error(methodologyRefusal(e, lang) || toErrMsg(e, t("signOff.signoffFailed")));   // STEP 7e-2 · S3: the deviations named, EN / AR
    } finally { setSigningStage(null); }
  };

  const doUnlock = async () => {
    if (!engagement) return;
    setUnlocking(true);
    try {
      const fd = new FormData();
      fd.append("reason", unlockReason);
      await api.post(`/engagements/${engagement.id}/unlock`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("signOff.unlockSuccess"));
      setUnlockOpen(false);
      setUnlockReason("");
      onChanged && onChanged();
    } catch (e) {
      toast.error(toErrMsg(e, t("signOff.unlockFailed")));
    } finally { setUnlocking(false); }
  };

  const dl = (kind) => {
    const token = localStorage.getItem("af_token");
    const url = `${api.defaults.baseURL}/engagements/${engagement.id}/signoff/${kind}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = kind === "bundle"
          ? `${(engagement.engagement_name || engagement.client_name || "engagement").replace(/\s+/g, "_")}_signed_bundle.zip`
          : `${(engagement.engagement_name || engagement.client_name || "engagement").replace(/\s+/g, "_")}_signed_bundle.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  if (!engagement) return null;

  if (engagement.signed_off) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/20 dark:border-emerald-900"
             data-testid="signoff-card-locked">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
            <ShieldCheck className="h-4 w-4" /> {t("signOff.lockedTitle")}
          </CardTitle>
          {canManage && (
            <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="signoff-unlock-btn">
                  <Unlock className="me-1 h-3 w-3" /> {t("signOff.unlock")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("signOff.unlockTitle")}</DialogTitle>
                  <DialogDescription>
                    {t("signOff.unlockDesc")}
                  </DialogDescription>
                </DialogHeader>
                <div>
                  <Textarea placeholder={t("signOff.unlockPh")}
                             value={unlockReason}
                             onChange={(e) => setUnlockReason(e.target.value)}
                             data-testid="signoff-unlock-reason" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setUnlockOpen(false)}>{t("signOff.cancel")}</Button>
                  <Button onClick={doUnlock} disabled={unlocking}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="signoff-unlock-confirm">
                    {unlocking ? t("signOff.unlocking") : t("signOff.unlockBtn")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("signOff.signedBy")}</div>
              <div className="font-medium">{engagement.signed_off_by_name}</div>
              <div className="text-xs text-muted-foreground">{engagement.signed_off_by_role}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("signOff.signedAt")}</div>
              <div className="font-mono text-xs">{fmtDateTimeFirm(engagement.signed_off_at)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Fingerprint className="h-3 w-3" /> {t("signOff.integrity")}
              </div>
              <div className="font-mono text-[10px] break-all">{engagement.signature_hash}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => dl("bundle")}
                    data-testid="signoff-dl-zip">
              <FileArchive className="me-1 h-3 w-3" /> {t("signOff.dlBundle")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => dl("pdf")}
                    data-testid="signoff-dl-pdf">
              <FileText className="me-1 h-3 w-3" /> {t("signOff.dlPdf")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---- Sequential per-stage sign-off ----
  return (
    <Card className="border-slate-200 dark:border-slate-800" data-testid="signoff-card-open">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Lock className="h-4 w-4 text-slate-500" />
          {t("signOff.workflowTitle")}{engagement.requires_eqc ? t("signOff.eqcSuffix") : ""}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Batch A · R2 — one-line spec header (EN/AR) shown on every
             open sign-off card. */}
        <p className="text-xs font-medium text-slate-700 dark:text-slate-200"
           data-testid="signoff-header">
          {t("signoff.header")}
        </p>
        {gateBlocked && (
          <div className="flex items-start gap-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/30"
               data-testid={gateNoFramework ? "signoff-framework-blocker" : "signoff-mapping-blocker"}>
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-red-700" />
            <div className="flex-1">
              <div className="font-semibold text-red-900 dark:text-red-200">
                {gateNoFramework ? t("sdg4.preflight.noFrameworkTitle")   /* A-15 */
                  : t("signOff.blockedTitle").replace("{plural}", fmtPlural(preflight.unmapped_count, lang, dict.plurals.accounts))}
              </div>
              <div className="text-xs text-red-800 dark:text-red-300">
                {gateNoFramework ? gateReason : t("signOff.blockedDesc").replace("{fw}", preflight.framework)}
              </div>
              {preflight.unmapped?.length > 0 && (
                <div className="mt-1 text-[11px] text-red-800/80 dark:text-red-300/80 font-mono">
                  {preflight.unmapped.slice(0, 5).map((u) => u.raw_account_code || u.raw_account_name).join(", ")}
                  {preflight.unmapped_count > 5 ? t("signOff.moreSuffix").replace("{n}", String(preflight.unmapped_count - 5)) : ""}
                </div>
              )}
            </div>
            <Link to={`/ifrs/${engagement.id}/mapping`}>
              <Button size="sm" variant="outline" data-testid="signoff-open-mapping">
                <ExternalLink className="me-1 h-3 w-3" /> {t("signOff.openMapping")}
              </Button>
            </Link>
          </div>
        )}
        {/* ISQM 2.24(b), 27 report-date gate — Partner-stage visibility */}
        {eqrBlocked && (
          <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30"
               data-testid="signoff-eqr-blocker">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-700" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900 dark:text-amber-200">
                {t("signOff.eqrBlockedTitle")}
              </div>
              <div className="text-xs text-amber-800 dark:text-amber-300">
                {t("signOff.eqrBlockedDesc").split("{satisfactorily}").map((chunk, idx, arr) => (
                  <React.Fragment key={idx}>
                    {chunk}
                    {idx < arr.length - 1 && <b>{t("signOff.satisfactorilyBold")}</b>}
                  </React.Fragment>
                ))}
                {eqr?.conclusion_text ? (
                  <> {t("signOff.currentValue")} <span className="font-mono">{`"${eqr.conclusion_text}"`}</span>.</>
                ) : (
                  <>{t("signOff.noConclusion")}</>
                )}
                {/* Silent defaults · A-14: the server says why the text does not count (exact option value only) */}
                {eqr?.message ? <div className="mt-1" data-testid="signoff-eqr-522a-message">{(lang === "ar" && eqr.message_ar) || eqr.message}</div> : null}
              </div>
            </div>
            <Link to={`/isqm/engagement/${engagement.id}/form/azex_522a`}>
              <Button size="sm" variant="outline" data-testid="signoff-open-522a">
                <ExternalLink className="me-1 h-3 w-3" /> {t("signOff.open522a")}
              </Button>
            </Link>
          </div>
        )}
        {eqrOk && (
          <div className="flex items-start gap-3 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm dark:border-emerald-900 dark:bg-emerald-950/30"
               data-testid="signoff-eqr-ok">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-emerald-700" />
            <div className="flex-1">
              <div className="font-semibold text-emerald-900 dark:text-emerald-200">
                {t("signOff.eqrOkTitle")}
              </div>
              <div className="text-xs text-emerald-800 dark:text-emerald-300">
                {eqr?.reviewer_signature ? (
                  <>{t("signOff.eqrSignedOffBy")} <b>{eqr.reviewer_signature}</b></>
                ) : (
                  <>{t("signOff.eqrConfirmed")}</>
                )}
                {eqr?.last_updated_at
                  ? <>{t("signOff.eqrOnDate").replace("{when}", "")}<span className="font-mono">{fmtDateTimeFirm(eqr.last_updated_at)}</span></>
                  : null}
                {eqr?.last_updated_by_name ? <>{t("signOff.eqrRecordedBy").replace("{who}", eqr.last_updated_by_name)}</> : null}.
                {eqr?.significant_matters ? (
                  <div className="mt-1"><b>{t("signOff.significantMatters")}</b> {eqr.significant_matters}</div>
                ) : null}
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {stageOrder.map((stage, idx) => {
            const meta = STAGE_META[stage];
            const stageLabel = t(meta.labelKey);
            const state = wf[stage];
            const isSigned = !!state?.signed;
            const previousSigned = idx === 0 || !!wf[stageOrder[idx - 1]]?.signed;
            const right = rights ? rights[stage] : null;
            const canISignThis = !!right?.ok;
            const whyNot = right && !right.ok ? ((lang === "ar" && right.reason_ar) || right.reason) : "";
            const nextInLine = !isSigned && previousSigned;
            return (
              <div key={stage}
                   className={`rounded-md border p-3 ${isSigned
                     ? "border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-900"
                     : nextInLine
                       ? "border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/20 dark:border-emerald-900"
                       : "border-slate-200 bg-slate-50/40 dark:bg-slate-900/40"}`}
                   data-testid={`signoff-stage-${stage}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {isSigned ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <Lock className="h-3.5 w-3.5 text-slate-400" />}
                    <span>{idx + 1}. {stageLabel}</span>
                  </div>
                  {isSigned && (
                    <span className="text-[10px] text-emerald-700">
                      {t("signOff.stageSigned")}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">{t(meta.hintKey)}</div>
                {isSigned ? (
                  <div className="mt-2 text-[11px]">
                    <div><b>{state.by_name}</b> <span className="text-muted-foreground">{`(${state.by_role})`}</span></div>
                    <div className="text-muted-foreground font-mono">{fmtDateTimeFirm(state.at)}</div>
                  </div>
                ) : nextInLine ? (
                  canISignThis ? (
                    <Button size="sm"
                            className={`mt-2 text-white ${stage === "partner" && gateBlocked
                              ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed"
                              : "bg-emerald-600 hover:bg-emerald-700"}`}
                            onClick={() => doStageSignoff(stage)}
                            disabled={signingStage === stage
                                       || (stage === "partner" && gateBlocked)}
                            title={stage === "partner" && gateBlocked
                              ? gateReason
                              : undefined}
                            data-testid={`signoff-stage-btn-${stage}`}>
                      {signingStage === stage
                        ? t("signOff.stageSigning")
                        : (stage === "partner" && gateBlocked
                          ? t("signOff.stagePartnerBlocked")
                          : t("signOff.stageSignAs").replace("{stage}", stageLabel))}
                    </Button>
                  ) : (
                    <div className="mt-2 text-[11px] italic text-muted-foreground" data-testid={`signoff-stage-why-${stage}`}>
                      {whyNot || t("s3r8.stageRightsLoading")}
                    </div>
                  )
                ) : (
                  <div className="mt-2 text-[11px] italic text-muted-foreground">
                    {t("signOff.stagePending")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <LockPreflightDialog engId={engagement.id} open={lockAsk} onClose={() => setLockAsk(false)}
                             onProceed={() => doStageSignoff("partner", true)} />
      </CardContent>
    </Card>
  );
}


/** Read-only banner shown on every mutation page when the current engagement is locked. */
export function LockedBanner({ engagement }) {
  const { t } = useI18n();
  if (!engagement?.signed_off) return null;
  const when = fmtDateTimeFirm(engagement.signed_off_at);
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm dark:border-amber-800 dark:bg-amber-950/30"
         data-testid="locked-banner">
      <Lock className="h-4 w-4 text-amber-700" />
      <div className="flex-1">
        <span className="font-medium text-amber-900 dark:text-amber-200">{t("signOff.lockedBanner")}</span>
        <span className="ms-2 text-xs text-amber-800 dark:text-amber-300">
          {t("signOff.lockedBannerBy")
            .replace("{name}", engagement.signed_off_by_name || "")
            .replace("{when}", when)
            .replace("{zone}", firmZone())
            .replace("{hash}", (engagement.signature_hash || "").slice(0, 12))}
        </span>
      </div>
    </div>
  );
}
